import React, { useEffect, useRef } from "react";

import { A4_WIDTH, A4_HEIGHT } from "../constants/layoutConstants";
import { renderPageToCanvas } from "../onboardingUtils/pdfProcessor";

export const CanvasPageRenderer = ({
  arrayBuffer,
  pageNumber,
  width = A4_WIDTH,
  height = A4_HEIGHT,
  className = "",
  isBlankDocument = false,
}) => {
  const canvasRef = useRef(null);
  const renderIdRef = useRef(0);

  useEffect(() => {
    const currentRenderId = ++renderIdRef.current;

    const renderPage = async () => {
      if (!canvasRef.current || !pageNumber) {
        console.warn("CanvasPageRenderer: No canvas or pageNumber");
        return;
      }

      const ctx = canvasRef.current.getContext("2d");

      try {
        // Handle blank documents
        if (isBlankDocument) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
          return;
        }

        // Check if arrayBuffer exists and is valid
        if (!arrayBuffer) {
          console.warn("CanvasPageRenderer: No arrayBuffer provided");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
          ctx.fillStyle = "#666";
          ctx.font = "14px sans-serif";
          ctx.textAlign = "center";
          ctx.fillText("No PDF data available", width / 2, height / 2);
          return;
        }

        // Check if it's a valid ArrayBuffer
        if (!(arrayBuffer instanceof ArrayBuffer)) {
          console.error(
            "CanvasPageRenderer: Invalid buffer type:",
            typeof arrayBuffer
          );
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
          ctx.fillStyle = "#ef4444";
          ctx.font = "14px sans-serif";
          ctx.textAlign = "center";
          ctx.fillText("Invalid PDF buffer", width / 2, height / 2);
          return;
        }

        // Check if buffer is not empty
        if (arrayBuffer.byteLength === 0) {
          console.warn("CanvasPageRenderer: Empty arrayBuffer");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
          ctx.fillStyle = "#666";
          ctx.font = "14px sans-serif";
          ctx.textAlign = "center";
          ctx.fillText("Empty PDF buffer", width / 2, height / 2);
          return;
        }

        // Render the PDF page
        if (currentRenderId === renderIdRef.current) {
          await renderPageToCanvas(arrayBuffer, pageNumber, canvasRef.current);
        }
      } catch (error) {
        console.error("CanvasPageRenderer error:", error);
        if (canvasRef.current && currentRenderId === renderIdRef.current) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
          ctx.fillStyle = "#ef4444";
          ctx.font = "14px sans-serif";
          ctx.textAlign = "center";
          ctx.fillText("Error loading PDF", width / 2, height / 2);
          ctx.font = "12px sans-serif";
          ctx.fillText(
            error.message || "Unknown error",
            width / 2,
            height / 2 + 20
          );
        }
      }
    };

    renderPage();
  }, [arrayBuffer, pageNumber, width, height, isBlankDocument]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={className}
      style={{ display: "block" }}
    />
  );
};

export default CanvasPageRenderer;
